import { useState, type FormEvent } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/services/http";

export default function PortalRegisterPage() {
  const { register, isAuthenticated, isLoading, isDemoMode } = useAuth();
  const navigate = useNavigate();

  const [name,     setName]     = useState("");
  const [email,    setEmail]    = useState("");
  const [phone,    setPhone]    = useState("");
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [showPass, setShowPass] = useState(false);
  const [agreed,   setAgreed]   = useState(false);
  const [error,    setError]    = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isLoading && isAuthenticated) return <Navigate to="/account" replace />;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 8)  { setError("Password must be at least 8 characters."); return; }
    if (!agreed) { setError("Please agree to the terms to continue."); return; }

    setSubmitting(true);
    try {
      await register({ name, email, phone: phone || undefined, password });
      navigate("/account", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "h-11 w-full rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";
  const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground-600";

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-primary-900 p-12 lg:flex lg:w-[420px]">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Vibrant%20Indian%20coastal%20market%20street%20with%20colourful%20storefronts%2C%20vendors%20and%20busy%20commerce%20activity%2C%20warm%20daylight%2C%20editorial%20travel%20photography&width=840&height=1200&seq=portal-register-bg-01&orientation=portrait"
            alt=""
            className="h-full w-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-900/80 via-primary-900/60 to-primary-900/90" />
        </div>

        <div className="relative">
          <Link to="/" className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-400">
              <i className="ri-building-2-line text-xl text-foreground-950" />
            </span>
            <div>
              <p className="text-sm font-bold text-background-50">Coastal Bizz Connect</p>
              <p className="text-[11px] text-background-400">Owner Portal</p>
            </div>
          </Link>
        </div>

        <div className="relative space-y-6">
          <h1 className="font-heading text-3xl font-bold leading-tight text-background-50">
            Start reaching more customers across 3 coastal districts.
          </h1>
          <ul className="space-y-3">
            {[
              { icon: "ri-check-double-line", text: "Free listing — 3 photos across 3 categories, valid for 12 months" },
              { icon: "ri-check-double-line", text: "Business plan from ₹599/year" },
              { icon: "ri-check-double-line", text: "Premium plan with featured placement" },
            ].map((item) => (
              <li key={item.text} className="flex items-start gap-3 text-sm text-background-300">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center text-accent-400">
                  <i className={`${item.icon} text-sm`} />
                </span>
                {item.text}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-background-500">© {new Date().getFullYear()} Coastal Bizz Connect</p>
      </div>

      {/* Right form */}
      <div className="flex flex-1 flex-col items-center justify-center bg-background-50 px-6 py-12">
        <div className="w-full max-w-sm">
          {isDemoMode && (
            <div className="mb-6 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm">
              <p className="flex items-center gap-2 font-semibold text-accent-800">
                <i className="ri-information-line" /> Demo Mode
              </p>
              <p className="mt-1 text-accent-700">Any data entered here is for preview only.</p>
            </div>
          )}

          <div className="mb-8">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">Create your account</h2>
            <p className="mt-1.5 text-sm text-foreground-600">Join hundreds of coastal Karnataka businesses.</p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className={labelClass} htmlFor="reg-name">Full name</label>
              <input id="reg-name" type="text" required autoComplete="name" value={name}
                onChange={(e) => setName(e.target.value)} placeholder="Shrinivas Kamath" className={inputClass} />
            </div>

            <div>
              <label className={labelClass} htmlFor="reg-email">Email address</label>
              <input id="reg-email" type="email" required autoComplete="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inputClass} />
            </div>

            <div>
              <label className={labelClass} htmlFor="reg-phone">Phone number <span className="normal-case font-normal text-foreground-400">(optional)</span></label>
              <input id="reg-phone" type="tel" autoComplete="tel" value={phone}
                onChange={(e) => setPhone(e.target.value)} placeholder="+91 98450 00000" className={inputClass} />
            </div>

            <div>
              <label className={labelClass} htmlFor="reg-password">Password</label>
              <div className="relative">
                <input id="reg-password" type={showPass ? "text" : "password"} required
                  autoComplete="new-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="Min. 8 characters"
                  className={`${inputClass} pr-10`} />
                <button type="button" onClick={() => setShowPass((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-foreground-400 hover:text-foreground-700">
                  <i className={showPass ? "ri-eye-off-line text-base" : "ri-eye-line text-base"} />
                </button>
              </div>
            </div>

            <div>
              <label className={labelClass} htmlFor="reg-confirm">Confirm password</label>
              <input id="reg-confirm" type={showPass ? "text" : "password"} required
                autoComplete="new-password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat your password" className={inputClass} />
            </div>

            <label className="flex cursor-pointer items-start gap-3">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 cursor-pointer rounded accent-primary-500" />
              <span className="text-xs text-foreground-600">
                I agree to the{" "}
                <Link to="/about" className="text-primary-600 hover:underline">Terms of Service</Link>
                {" "}and understand that my listing will be reviewed before going live.
              </span>
            </label>

            {error && (
              <div className="flex items-start gap-2.5 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
                <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />
                {error}
              </div>
            )}

            <button type="submit" disabled={submitting}
              className="mt-1 inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60">
              {submitting ? (
                <><i className="ri-loader-4-line animate-spin text-base" /> Creating account…</>
              ) : (
                <><i className="ri-user-add-line text-base" /> Create Account</>
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-foreground-600">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}